import { useEffect, useRef } from "react";
import { useLocation, useSearch } from "wouter";

/*
 * Picking up where you left off. The address holds the page and its open windows and tabs (e.g. the
 * Price List's ?item=...&itemTab=pricing), so a browser refresh keeps them; but ServiceM8 opens the
 * app afresh at its starting page (Home, or a tool's page from its menu item) each time, refresh
 * included. So the last address is kept in this browser (per person and business), and arriving at a
 * starting page with nothing else in the address goes back to it, if it's recent and within the
 * page arrived at (a tool's menu item only goes back within that tool). Unsaved changes aren't kept.
 */

/** Older than this, it starts afresh: the next day's work starts at the start. */
const KEEP_MS = 12 * 60 * 60 * 1000;

interface Place {
    href: string;
    at: number;
}

const keyOf = (userId: string, companyId: string) => `last-place:${userId}:${companyId}`;

function read(key: string): Place | null {
    try {
        const place = JSON.parse(localStorage.getItem(key) ?? "null") as Place | null;
        return place && typeof place.href === "string" && typeof place.at === "number" ? place : null;
    } catch {
        return null;
    }
}

function write(key: string, place: Place) {
    try {
        localStorage.setItem(key, JSON.stringify(place));
    } catch {
        // Storage unavailable (private mode): nothing to go back to next time.
    }
}

/** The address this page load arrived at (read before anything moves it). Only that can go back: moving around the app never does. */
const arrival = globalThis.location ? globalThis.location.pathname + globalThis.location.search : "";
let arrived = false;

/**
 * Where to go back to on arriving at `href` (the full address), or null: not a starting page (Home or a
 * tool's page, nothing more in the address), nothing kept, too old, or not within it.
 */
export function placeToRestore(href: string, companyId: string, place: Place | null, now = Date.now()): string | null {
    const [path = "", search] = href.split("?");
    const start = path.replace(/\/$/, "");
    const home = `/c/${companyId}`;
    const atStart = !search && (start === home || /^\/t\/[a-z0-9-]+$/.test(start.slice(home.length)));
    if (!atStart || !place || now - place.at > KEEP_MS || place.href === href) return null;
    return place.href === start || place.href.startsWith(`${start}/`) || place.href.startsWith(`${start}?`) ? place.href : null;
}

/** Keep this person's place in this business as they move, and go back to it on arriving afresh (see above). */
export function useLastPlace(userId: string, companyId: string) {
    const [location, navigate] = useLocation();
    const search = useSearch();
    const key = keyOf(userId, companyId);
    const ready = useRef(false);

    useEffect(() => {
        const href = globalThis.location.pathname + globalThis.location.search;
        if (!ready.current) {
            ready.current = true;
            if (!arrived) {
                arrived = true;
                const back = href === arrival ? placeToRestore(href, companyId, read(key)) : null;
                // "~": the whole address, not within this business's routes.
                if (back) return navigate(`~${back}`, { replace: true });
            }
        }
        if (href.startsWith(`/c/${companyId}`)) write(key, { href, at: Date.now() });
    }, [key, companyId, navigate, location, search]);
}
